/**
 * use-panel-fixtures — Agent C Sprint Visual Brief 2A r7b.
 *
 * Hooks fixture-based para painel direito até backend materializar.
 * Espelha shapes canônicos de `design-references/brief-2a/r7a/scenario.jsx`
 * (SCENARIO_R7A) e r7b (SCENARIO_R7B).
 *
 * **R15 Triangulação — Items candidatos backend pendency:**
 *   - Item NEW-C1: ADR-105 CustomerCommitment endpoint REST. **RESOLVIDO**
 *     em Sprint Backend Operacional X — useCustomerCommitments agora consome
 *     `GET /v1/conversations/:id/commitments` real (api-client.listCustomerCommitments).
 *   - Item NEW-C2: ADR-108 ConversationIntent endpoint REST.
 *   - Item NEW-C3: ADR-109 Pendency endpoint REST.
 *   - Item NEW-C4: ADR-115 Appointment endpoint REST. ✅ RESOLVIDO em Sprint
 *     Backend Operacional Brief 2A r7b (Agent Y) — `useAppointments` agora
 *     consome `GET /v1/conversations/:id/appointments` real.
 *   - Item NEW-C3: ADR-109 Pendency endpoint REST. ✅ RESOLVIDO Sprint Backend Operacional Agent Z.
 *   - Item NEW-C4: ADR-115 Appointment endpoint REST.
 *   - Item NEW-C5: ADR-055 SequenceTimeline endpoint preview.
 *   - Item NEW-C6: SuperGer ContextBlock query (RAG + internal + SuperGer).
 *
 * Frontend tolera defensivamente — fixtures hard-coded retornam listas vazias
 * para conversation IDs não-conhecidas (não-quebra UI quando backend liga).
 *
 * Pattern espelha `useAdversarial` / `useSequence` (Wave 2 PR #46 Agent B) —
 * client-side adaptation pending backend materialization.
 */

import { formatDeadlinePT } from '@lume/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';


import { useCapability } from '../../../hooks/use-capability';
import { api, ApiError } from '../../../lib/api-client';
import { personWaitlistQuery } from '../../../lib/person-waitlist-query';
import { useToastStore } from '../../../stores/toast-store';

import { labelForIntentAction } from './intent-labels';

import type { AppointmentWireItem, PendencyWireItem } from '../../../lib/api-client';
import type { ConversationDetail } from '../../../lib/types';

/** ADR-105 CustomerCommitment — 5 estados canônicos */
export type CommitmentState = 'pending' | 'fulfilled' | 'broken' | 'cancelled' | 'unclear';

/** Tipos canônicos de CustomerCommitment (origem r7a scenario.jsx) */
export type CommitmentType = 'financial' | 'document' | 'action' | 'other';

export interface CustomerCommitmentFixture {
  id: string;
  type: CommitmentType;
  title: string;
  excerpt: string;
  state: CommitmentState;
  detectedAtFull: string;
  dueAt?: string;
  dueWarning?: string;
  confidence: number;
  fulfilledAt?: string;
  aiSuggestion?: string;
  /** Optimistic-concurrency cursor (ADR-071) — exigido pelas ações de update. */
  version: number;
}

/** ADR-115 Appointment — 5 estados canônicos */
export type AppointmentState =
  | 'tentative'
  | 'confirmed-appointment'
  | 'in-progress-appt'
  | 'completed'
  | 'cancelled-appt';

export interface AppointmentFixture {
  id: string;
  title: string;
  type: string;
  state: AppointmentState;
  when: string;
  isImminent: boolean;
  address: string;
  tech: string;
  /**
   * Frente B Sessão 2 — nomes dos recursos que ATENDEM (profissional/sala/cadeira),
   * derivados de `appointmentResources`. Mundo recurso-cêntrico: "quem atende" é o
   * recurso, não quem criou. `[]` para legados/avulsos → o card recai em `tech`
   * (inerte fora de scheduling-native).
   */
  resourceNames: string[];
  /** Frente B Bloco II — pertence a uma série de recorrência (badge "Série"). */
  isSeries: boolean;
  /** Optimistic-concurrency cursor (ADR-071) — exigido pelas ações de update. */
  version: number;
  /**
   * ADR-155 Fase 4 (E1/§3.1) — quantidade de campos no envelope episódico
   * (`Appointment.collectedData`). `0` sem coleta ainda. O mini-card mostra só
   * a contagem ("N dado(s) desta consulta") — o detalhe completo fica no
   * Drawer/agenda (não duplica o valor aqui).
   */
  collectedCount: number;
  /** ADR-155 Fase 4 (E3/§3.1) — terminal (completed/cancelled/no_show) não pode
   *  virar foco (o servidor rejeitaria com 422 `episode_focus_terminal`). */
  isTerminal: boolean;
}

/** ADR-109 Pendency — 4 estados canônicos */
export type PendencyState = 'open' | 'in-progress-pendency' | 'resolved' | 'cancelled-pendency';

export interface PendencyFixture {
  id: string;
  title: string;
  excerpt?: string;
  state: PendencyState;
  assignedTo: string;
  dueAt: string;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  /** Optimistic-concurrency cursor (ADR-071) — exigido pelas ações de update. */
  version: number;
  /**
   * `Pendency.source` cru (ADR-109 — string livre). É o discriminador de QUEM
   * prometeu: `conversation-promise` (o operador/IA ao cliente) × `customer-promise`
   * (o cliente) × `manual` (registro à mão) × ~10 outros de subsistemas que não são
   * promessa nenhuma. Traduzido por `provenanceOf` — nunca interpretado no componente.
   */
  source: string;
  /** Não-nulo quando a pendência nasceu de CAPTURA, não da mão. */
  aiAgentId: string | null;
  /**
   * De qual mensagem esta pendência nasceu (null fora de captura). É o que ancora a
   * linha «registrado · desfazer» na fala certa do fio — inclusive após um reload.
   */
  sourceMessageId: string | null;
}

/** ADR-055 ConversationSequence — nó da timeline cross-canal. */
export interface SequenceTimelineNode {
  id: string;
  position: number;
  /** Canal da conversa do nó — opcional: o endpoint de sequences não devolve por membro (item de backend). */
  channel?: string;
  startedAt: string;
  /** Status da conversa do nó — opcional pelo mesmo motivo de `channel`. */
  status?: string;
  outcome?: string | null;
  isCurrent: boolean;
}

// Item NEW-C6 RESOLVIDO via sub-fatia 10.0 PR 3 (ADR-142):
// `ContextFixture` + `useCustomerContext` fixture removidos. Consumidores
// usam `useCustomerContext` real em `hooks/use-customer-context.ts`.

/**
 * Hook canonical para CustomerCommitments — Item NEW-C1 RESOLVIDO via
 * Sprint Backend Operacional X (ADR-105).
 *
 * Consome `GET /v1/conversations/:id/commitments` real e adapta wire type
 * `SerializedCustomerCommitment` → shape canonical `CustomerCommitmentFixture`
 * preservando contrato com `CustomerCommitmentBlock.tsx`.
 *
 * Pattern espelha `useConflictAlerts` (PR-B.2 Wave 2):
 *   - staleTime 60s (commitments mudam em baixa frequência).
 *   - queryKey por conversationId.
 *   - Fallback array vazio quando loading/error (UI mostra empty state).
 */
export function useCustomerCommitments(conversation: ConversationDetail): {
  items: CustomerCommitmentFixture[];
  isLoading: boolean;
  isEmpty: boolean;
} {
  const conversationId = conversation.id;
  const queryResult = useQuery({
    queryKey: ['customer-commitments', conversationId],
    enabled: typeof conversationId === 'string' && conversationId.length > 0,
    queryFn: () => api.listCustomerCommitments({ conversationId }),
    staleTime: 60_000,
  });

  if (queryResult.isLoading) {
    return { items: [], isLoading: true, isEmpty: false };
  }

  const wire = queryResult.data?.data ?? [];
  const items: CustomerCommitmentFixture[] = wire.map((c) => {
    const fixture: CustomerCommitmentFixture = {
      id: c.id,
      type: c.type,
      title: c.title,
      excerpt: c.quote ?? '',
      state: c.status,
      detectedAtFull: formatRelativeTimePT(c.detectedAt),
      confidence: c.aiConfidence ?? 0,
      version: c.version,
    };
    if (c.dueDate !== null) {
      fixture.dueAt = formatDuePT(c.dueDate);
    }
    if (c.fulfilledAt !== null) {
      fixture.fulfilledAt = formatRelativeTimePT(c.fulfilledAt);
    }
    // r7a UI hint: confidence < 0.80 → sugere promover (status=unclear).
    if (c.status === 'unclear') {
      fixture.aiSuggestion = 'promover para pending';
    }
    // dueWarning canonical: < 7 dias até dueDate.
    if (c.dueDate !== null && c.status === 'pending') {
      const daysUntilDue = daysUntil(c.dueDate);
      if (daysUntilDue !== null && daysUntilDue <= 7 && daysUntilDue >= 0) {
        fixture.dueWarning = `Vence em ${daysUntilDue.toString()} dia${daysUntilDue === 1 ? '' : 's'}`;
      }
    }
    return fixture;
  });

  return { items, isLoading: false, isEmpty: items.length === 0 };
}

/**
 * Hook canonical para Appointments — Item NEW-C4 backend pendency RESOLVIDO.
 * Formato relativo PT-BR: "há 6 min" / "há 2 h" / "há 1 dia".
 * Espelha SCENARIO_R7A.customerCommitments.detectedAtFull canonical.
 */
function formatRelativeTimePT(isoDate: string): string {
  const then = new Date(isoDate).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / (60 * 1000));
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin.toString()} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `há ${diffHours.toString()} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `há ${diffDays.toString()} dia${diffDays === 1 ? '' : 's'}`;
}

/**
 * Formato due PT-BR: "20/06/2026" para dueDate ISO; «amanhã» na janela de 36h.
 *
 * Mudou de casa em 15/Ago/2026 (`@lume/shared`, `pendencies/deadline-format`)
 * quando o app móvel passou a escrever prazo no cartão de pendência. Corpo
 * preservado byte a byte — inclusive a saída em UTC, cuja limitação está
 * declarada no docblock de lá.
 */
const formatDuePT = formatDeadlinePT;

/**
 * Dias até dueDate (UTC midnight diff). Null se já passou.
 */
function daysUntil(isoDate: string): number | null {
  const due = new Date(isoDate);
  const now = Date.now();
  const diffMs = due.getTime() - now;
  if (diffMs < 0) return null;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

/**
 * Hook canonical para Appointments — Item NEW-C4 backend pendency.
 *
 * Sprint Backend Operacional Brief 2A r7b (Agent Y) materializou `Appointment`
 * end-to-end via ADR-115. Hook agora consome `GET /v1/conversations/:id/appointments`
 * real (react-query) e adapta wire → fixture shape canonically:
 *   - `status: 'imminent'` → `state: 'confirmed-appointment'` + `isImminent: true`
 *   - `status: 'confirmed'` → `state: 'confirmed-appointment'` + `isImminent: false`
 *   - `status: 'scheduled'` → `state: 'tentative'` + `isImminent: false`
 *   - `status: 'completed'` → `state: 'completed'`
 *   - `status: 'cancelled'` / `'no_show'` → `state: 'cancelled-appt'`
 *   - `status: 'rescheduled'` → omitido (filtrado client-side; cadeia mostra apenas a sucessora)
 *
 * Capability gate `appointments` (Solo+) é aplicado server-side; capability
 * faltante retorna 403 — frontend tolera defensivamente retornando lista vazia.
 *
 * StaleTime canonical 60s (espelha pattern useBond Wave 2 PR #45).
 */

const APPOINTMENTS_STALE_TIME_MS = 60_000;

/** Estados terminais do appointment — espelha `TERMINAL_APPOINTMENT_STATUSES`
 *  do engine (`dispatch-plan.ts`) no vocabulário de UI (`AppointmentState`).
 *  `'rescheduled'` fica de fora: já é filtrado antes de chegar aqui. */
const TERMINAL_STATES: ReadonlySet<AppointmentState> = new Set(['completed', 'cancelled-appt']);

/** Exportado — reusado por `useEpisodeCandidates` (ADR-155 Fase 4 E4) para
 *  adaptar os candidatos ao mesmo shape do mini-card. */
export function adaptAppointment(wire: AppointmentWireItem): AppointmentFixture | null {
  // Rescheduled é terminal — filtrado da listagem ativa.
  if (wire.status === 'rescheduled') return null;

  let state: AppointmentState;
  let isImminent = false;
  switch (wire.status) {
    case 'imminent':
      state = 'confirmed-appointment';
      isImminent = true;
      break;
    case 'confirmed':
      state = 'confirmed-appointment';
      break;
    case 'scheduled':
      state = 'tentative';
      break;
    case 'completed':
      state = 'completed';
      break;
    case 'cancelled':
    case 'no_show':
      state = 'cancelled-appt';
      break;
    default:
      // Status desconhecido — guard exaustivo defensivo.
      state = 'tentative';
  }

  // Format `when` canonical (display fixture-style).
  // ex: "Quinta · 15/Mai · 14:00 – 16:00"
  const startDate = new Date(wire.scheduledAt);
  const endDate = new Date(startDate.getTime() + wire.durationMinutes * 60_000);
  const weekdayFmt = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' });
  const dayFmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' });
  const timeFmt = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const weekday =
    weekdayFmt.format(startDate).charAt(0).toUpperCase() + weekdayFmt.format(startDate).slice(1);
  const day = dayFmt.format(startDate);
  const startTime = timeFmt.format(startDate);
  const endTime = timeFmt.format(endDate);
  const when = `${weekday} · ${day} · ${startTime} – ${endTime}`;

  return {
    id: wire.id,
    title: wire.title,
    // Adapt type snake → kebab.
    type: wire.type.replace(/_/g, '-'),
    state,
    when,
    isImminent,
    address: wire.addressOrLocation ?? '',
    tech: wire.notes ?? '',
    // Frente B Sessão 2 — "quem atende" = recurso(s); o backend denormaliza o nome
    // (a Conversa não carrega o catálogo de recursos). Filtra nulos (rollout/legado).
    resourceNames: (wire.appointmentResources ?? [])
      .map((r) => r.resourceName)
      .filter((n): n is string => n !== null && n.length > 0),
    isSeries: wire.seriesId !== null,
    version: wire.version,
    collectedCount: wire.collectedData !== null ? Object.keys(wire.collectedData).length : 0,
    isTerminal: TERMINAL_STATES.has(state),
  };
}

export function useAppointments(conversation: ConversationDetail): {
  items: AppointmentFixture[];
  isLoading: boolean;
  isEmpty: boolean;
} {
  const conversationId = conversation.id;

  const query = useQuery({
    queryKey: ['appointments', conversationId],
    enabled: conversationId !== undefined && conversationId.length > 0,
    staleTime: APPOINTMENTS_STALE_TIME_MS,
    queryFn: async (): Promise<AppointmentWireItem[]> => {
      try {
        const response = await api.listAppointments(conversationId);
        return response.data;
      } catch (err) {
        // 403 capability missing → tolera defensivamente lista vazia (não-quebra UI).
        if (err instanceof ApiError && err.status === 403) return [];
        throw err;
      }
    },
  });

  const wireItems = query.data ?? [];
  const items = wireItems.map(adaptAppointment).filter((x): x is AppointmentFixture => x !== null);

  return {
    items,
    isLoading: query.isLoading,
    isEmpty: items.length === 0,
  };
}

/**
 * ADR-155 Fase 4 (E4/§3.1) — candidatos a episódio EM FOCO fora da lista da
 * conversa ("Outros agendamentos do cliente"): mesmo raio de posse do
 * seletor manual (mesma conversa OU mesma pessoa OU mesmo bond),
 * não-terminais. **Gateia o FETCH** (não só o render — `feedback_gatear_
 * fetch_nao_so_render`) por `enabled`: a subseção nasce colapsada — só busca
 * quando o operador a abre.
 */
export function useEpisodeCandidates(
  conversation: ConversationDetail,
  enabled: boolean,
): { items: AppointmentFixture[]; isLoading: boolean } {
  const conversationId = conversation.id;

  const query = useQuery({
    queryKey: ['episode-candidates', conversationId],
    enabled: enabled && conversationId.length > 0,
    staleTime: APPOINTMENTS_STALE_TIME_MS,
    queryFn: async (): Promise<AppointmentWireItem[]> => {
      try {
        const response = await api.listEpisodeCandidates(conversationId);
        return response.data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) return [];
        throw err;
      }
    },
  });

  const wireItems = query.data ?? [];
  const items = wireItems.map(adaptAppointment).filter((x): x is AppointmentFixture => x !== null);

  return { items, isLoading: enabled && query.isLoading };
}

/**
 * ADR-155 Fase 4 (E3/§3.1) — foco MANUAL do episódio da conversa ("Focar
 * coleta aqui"). Em sucesso invalida o detail (foco+paralelas mudam) e a
 * lista de appointments (a chip `em foco` do mini-card lê do detail, não
 * da lista — mas invalida os dois por segurança/consistência de cache).
 * Em erro (422 `episode_focus_terminal`/`episode_focus_not_owned`) mostra
 * o motivo honesto vindo do servidor.
 */
export function useSetEpisodeFocus(conversationId: string): {
  focus: (appointmentId: string | null) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (appointmentId: string | null) =>
      api.patchEpisodeFocus(conversationId, appointmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['appointments', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['episode-candidates', conversationId] });
    },
    onError: (err) => {
      const reason =
        err instanceof ApiError && err.body.reason !== undefined
          ? err.body.reason
          : err instanceof Error
            ? err.message
            : 'Tente novamente.';
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível focar a coleta aqui',
        description: reason,
        durationMs: 6000,
      });
    },
  });

  return {
    focus: (appointmentId) => mutation.mutate(appointmentId),
    isPending: mutation.isPending,
  };
}

/**
 * Frente B Sessão 2 — resumo da FILA DE ESPERA deste cliente, exibível no painel da
 * Conversa. Fecha a assimetria: quando a IA enfileira o cliente NA conversa, o operador
 * não via (a fila só aparecia na /agenda, por recurso). Aqui ele vê "na fila de <serviço>".
 *
 * Status canônicos exibíveis (`QUEUE_OCCUPYING` no backend): `pending_approval` (aguarda
 * o operador liberar), `active` (na fila), `offered` (vaga ofertada, expira em X).
 */
export interface WaitlistSummaryFixture {
  id: string;
  serviceName: string;
  status: 'pending_approval' | 'active' | 'offered';
  /** ISO de expiração da oferta (só quando status='offered'); null caso contrário. */
  offerExpiresAt: string | null;
}

/**
 * Hook canonical da fila de espera por-pessoa. **Gateia o FETCH** (não só o render) por
 * `scheduling-waitlist` + `personId` presente — fora de tenant com fila, zero query (a
 * Conversa abre o tempo todo; não pagar query em quem não usa). `personId` é indexado no
 * backend → query seletiva e barata. 403 (cap ausente no servidor) degrada p/ vazio.
 */
export function usePersonWaitlist(conversation: ConversationDetail): {
  items: WaitlistSummaryFixture[];
  isLoading: boolean;
} {
  const hasCapability = useCapability('scheduling-waitlist');
  const personId = conversation.personId;
  const enabled = hasCapability && personId !== null;

  // Key+fn compartilhadas com a ficha da pessoa (contrato de cache — vide
  // docblock de `personWaitlistQuery`).
  const query = useQuery({
    ...personWaitlistQuery(personId),
    enabled,
    staleTime: APPOINTMENTS_STALE_TIME_MS,
  });

  if (!enabled || query.isError) {
    return { items: [], isLoading: false };
  }

  const items: WaitlistSummaryFixture[] = (query.data ?? [])
    .filter(
      (e): e is typeof e & { status: 'pending_approval' | 'active' | 'offered' } =>
        e.status === 'pending_approval' || e.status === 'active' || e.status === 'offered',
    )
    .map((e) => ({
      id: e.id,
      serviceName: e.serviceName ?? 'serviço',
      status: e.status,
      offerExpiresAt: e.offerExpiresAt,
    }));

  return { items, isLoading: query.isLoading };
}

/**
 * Hook canonical para Pendencies do atendente — Item NEW-C3 backend pendency.
 *
 * Pendências SUAS (do atendente) — ADR-109. Diferente de CustomerCommitments
 * (do cliente).
 *
 * **Sprint Backend Operacional Agent Z (19/Mai/2026):** substituída fixture
 * hardcoded por API real `GET /v1/conversations/:id/pendencies` —
 * `PendencyWireItem` wire types canonicais (status snake_case +
 * `overdue` flag derivada D109.2).
 *
 * Capability gate canônico: `pendencies` (Team+ via tier-filter — Solo NÃO
 * recebe — Solo opera individual sem coordenação peer).
 *
 * Sem capability → estado vazio defensivo (não quebra UI, mas painel apresenta
 * empty-state). Esse comportamento é aceitável v1: backend rejeita 403, hook
 * captura via `isError` e devolve `items: []` — Solo tenant não vê seção.
 *
 * Mapeamento canonical wire → fixture state:
 *   `in_progress` (wire) → `in-progress-pendency` (fixture/UI)
 *   `cancelled` (wire) → `cancelled-pendency` (fixture/UI)
 *   `expired` (wire) → mapped p/ `cancelled-pendency` (UI v1 não distingue;
 *                       refinement futuro pode adicionar variant visual).
 */
export function usePendencies(conversation: ConversationDetail): {
  items: PendencyFixture[];
  isLoading: boolean;
  isEmpty: boolean;
} {
  const hasCapability = useCapability('pendencies');
  const enabled = hasCapability && conversation.id.length > 0;

  const queryResult = useQuery({
    queryKey: ['pendencies', 'by-conversation', conversation.id],
    enabled,
    queryFn: () => api.listPendenciesByConversation(conversation.id),
    staleTime: 30_000,
  });

  if (!enabled) {
    return { items: [], isLoading: false, isEmpty: true };
  }
  if (queryResult.isLoading) {
    return { items: [], isLoading: true, isEmpty: false };
  }
  if (queryResult.isError) {
    return { items: [], isLoading: false, isEmpty: true };
  }

  // A aba mostra TRABALHO VIVO. Resolvida/cancelada/expirada/arquivada continuam
  // existindo (histórico, painel pessoal, fila do supervisor) — mas ficar na conversa
  // para sempre transforma a seção em entulho: cartão inerte que ninguém pode acionar
  // e que empurra o que importa para baixo da rolagem. `postponed` FICA: adiada segue
  // viva, só mudou de data.
  const wireItems = (queryResult.data?.data ?? []).filter((p) =>
    LIVE_PENDENCY_STATUSES.has(p.status),
  );
  const items: PendencyFixture[] = wireItems.map(toPendencyFixture);
  return { items, isLoading: false, isEmpty: items.length === 0 };
}

/** Estados em que a pendência ainda pede ação de alguém. */
const LIVE_PENDENCY_STATUSES: ReadonlySet<PendencyWireItem['status']> = new Set([
  'open',
  'in_progress',
  'postponed',
]);

/**
 * Adaptador canonical wire → fixture (Sprint Backend Operacional Agent Z).
 * Espelha campos minimal exigidos pelo PendencyBlock; `dueAt` e `createdAt`
 * vêm como ISO strings — UI renderiza com formatRelative quando materializar
 * (placeholder direto v1).
 */
function toPendencyFixture(p: PendencyWireItem): PendencyFixture {
  return {
    id: p.id,
    title: p.title,
    ...(p.description !== null && { excerpt: p.description }),
    state: wireStatusToFixtureState(p.status),
    assignedTo: p.delegatedToUserId ?? p.ownerUserId,
    // O adaptador entregava o ISO CRU e o cartão escrevia «até
    // 2026-08-21T21:00:00.000Z» na cara do operador. O docblock dizia «UI renderiza
    // com formatRelative quando materializar (placeholder direto v1)» — e o
    // placeholder sobreviveu porque nenhuma pendência de teste tinha prazo. Só
    // apareceu ao registrar uma promessa de verdade pela tela, em 14/Ago.
    dueAt: p.deadline !== null ? formatDuePT(p.deadline) : '',
    priority: p.priority === 'urgent' ? 'high' : p.priority,
    createdAt: p.createdAt,
    version: p.version,
    source: p.source,
    aiAgentId: p.aiAgentId,
    sourceMessageId: p.sourceMessageId,
  };
}

function wireStatusToFixtureState(status: PendencyWireItem['status']): PendencyState {
  switch (status) {
    case 'open':
      return 'open';
    case 'in_progress':
      return 'in-progress-pendency';
    case 'resolved':
      return 'resolved';
    case 'postponed':
      // Adiada — segue ativa/on-hold no painel em-conversa.
      return 'in-progress-pendency';
    case 'cancelled':
    case 'expired':
    case 'archived':
      return 'cancelled-pendency';
  }
}

/**
 * Intenção detectada pela IA — shape REAL que o backend produz (Item NEW-C2,
 * 22/Jun/2026). Lean por design: o `AIConductorTurn.payload.intentClassification`
 * só carrega `{ action, confidence }`. `secondaryIntents`/`aiRationale`/lifecycle/
 * confirmação NÃO existem no backend → honest-defer (não fabricamos no FE).
 */
export interface DetectedIntent {
  /** Rótulo amigável pt-BR da ação (via `labelForIntentAction`). */
  label: string;
  /** Ação canônica crua (`IntentAction` de `@lume/shared`, ou off-taxonomy). */
  rawAction: string;
  /** Confiança 0..1 emitida pelo conductor. */
  confidence: number;
  /** Quando foi detectado, relativo pt-BR ("há 6 min"). */
  detectedAtFull: string;
}

/**
 * Hook canonical para Intent — Item NEW-C2 RESOLVIDO (22/Jun/2026).
 *
 * Deriva a intenção do DETAIL já carregado (`conversation.detectedIntent`,
 * servido pelo serializer a partir do último `AIConductorTurn` com
 * `intentClassification`) — **sem fetch novo** (zero request adicional; o painel
 * já busca o detail e o React Query/WS invalida a cada turno → reatividade de
 * graça). `null` quando nenhum turno classificou (H_a, prompt sem step-up, ou
 * conversa nova): o card mostra estado honesto, NUNCA "nenhuma intenção
 * detectada" como fato.
 */
export function useIntent(conversation: ConversationDetail): {
  intent: DetectedIntent | null;
  isLoading: boolean;
} {
  const raw = conversation.detectedIntent;
  if (raw === null || raw === undefined) {
    return { intent: null, isLoading: false };
  }
  return {
    intent: {
      label: labelForIntentAction(raw.action),
      rawAction: raw.action,
      confidence: raw.confidence,
      detectedAtFull: formatRelativeTimePT(raw.detectedAt),
    },
    isLoading: false,
  };
}

/**
 * Hook canonical para SequenceTimeline — Item NEW-C5 RESOLVIDO (read-path).
 *
 * Consome `GET /v1/persons/:id/sequences?bondId=X&status=active` (Fatia F,
 * ADR-055) e resolve a sequence em que a conversa em foco é membro, mapeando
 * os `members` em nós ordenados (cross-canal mesmo Bond+Person).
 *
 * Usa a MESMA queryKey de `useSequence` (`['conversation-sequence', personId,
 * bondId]`) → compartilha o cache react-query (sem fetch duplicado) e herda as
 * invalidações WS daquele hook.
 *
 * **Limitação conhecida (item de backend):** o endpoint devolve por membro
 * apenas `{ conversationId, orderIndex, joinedAt }` — sem `channel`/`status`/
 * `outcome` por conversa. Portanto os nós saem sem canal/desfecho até o backend
 * enriquecer o payload de `members`.
 *
 * Pré-requisitos (espelha `useSequence`): `personId` + `bondId` presentes +
 * capability `conversation-sequences` (Team+). Faltando qualquer um → vazio.
 */
export function useSequenceTimeline(conversation: ConversationDetail): {
  nodes: SequenceTimelineNode[];
  totalConversations: number;
  isLoading: boolean;
} {
  const hasCapability = useCapability('conversation-sequences');
  const personId = conversation.personId;
  const bondId = conversation.bondId;
  const conversationId = conversation.id;
  const enabled = hasCapability && personId !== null && bondId !== null;

  const queryResult = useQuery({
    queryKey: ['conversation-sequence', personId, bondId],
    enabled,
    queryFn: () => {
      if (personId === null || bondId === null) return Promise.resolve({ data: [] });
      return api.listSequencesForPerson(personId, { bondId, status: 'active' });
    },
  });

  if (!enabled || queryResult.isError) {
    return { nodes: [], totalConversations: 0, isLoading: false };
  }
  if (queryResult.isLoading) {
    return { nodes: [], totalConversations: 0, isLoading: true };
  }

  const list = queryResult.data?.data ?? [];
  const active = list.find((s) => s.members.some((m) => m.conversationId === conversationId));
  if (active === undefined) {
    return { nodes: [], totalConversations: 0, isLoading: false };
  }

  const sorted = [...active.members].sort((a, b) => a.orderIndex - b.orderIndex);
  const nodes: SequenceTimelineNode[] = sorted.map((m, i) => ({
    id: m.conversationId,
    position: i + 1,
    startedAt: formatRelativeTimePT(m.joinedAt),
    isCurrent: m.conversationId === conversationId,
  }));

  return { nodes, totalConversations: active.totalConversations, isLoading: false };
}

// Item NEW-C6 RESOLVIDO via sub-fatia 10.0 PR 3 (ADR-142).
// `useCustomerContext` real vive em `hooks/use-customer-context.ts`.
